import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, Clock, Copy, Download, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import api, { money } from "../lib/api";

export default function OrderResultPage() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let timer;
    const fetchOrder = () => {
      api.get(`/orders/${id}`)
        .then((r) => {
          setOrder(r.data);
          if (r.data.status === "pending") timer = setTimeout(fetchOrder, 3000);
        })
        .catch((e) => { if (e?.response?.status === 404) setNotFound(true); });
    };
    fetchOrder();
    return () => clearTimeout(timer);
  }, [id]);

  if (notFound) {
    return (
      <div><Header />
        <main className="max-w-3xl mx-auto px-6 py-32 text-center text-zinc-500" data-testid="order-not-found">Pedido não encontrado.</main>
        <Footer />
      </div>
    );
  }
  if (!order) return <div className="min-h-screen bg-background" data-testid="order-loading" />;

  const paid = order.status === "paid";

  const copyCode = () => {
    navigator.clipboard.writeText(order.originality_code);
    toast.success("Código copiado");
  };

  return (
    <div data-testid="order-result-page">
      <Header />
      <main className="max-w-3xl mx-auto px-6 py-20 min-h-[60vh]">
        <div className="text-center mb-12 animate-fade-up">
          {paid ? (
            <>
              <CheckCircle2 size={56} className="text-gold mx-auto mb-6" />
              <h1 className="font-display text-4xl tracking-tighter font-medium" data-testid="order-paid-title">Pagamento confirmado</h1>
              <p className="text-zinc-400 mt-4">Pedido <span className="text-gold font-semibold" data-testid="order-number">#{String(order.order_number).padStart(4, "0")}</span> confirmado. Obrigado, {order.customer_name.split(" ")[0]}.</p>
            </>
          ) : (
            <>
              <Clock size={56} className="text-amber-500/70 mx-auto mb-6 animate-pulse" />
              <h1 className="font-display text-4xl tracking-tighter font-medium" data-testid="order-pending-title">Aguardando pagamento</h1>
              <p className="text-zinc-400 mt-4">Assim que a InfinitePay confirmar, seu pedido será numerado e liberado automaticamente.</p>
              {order.checkout_url && (
                <Button asChild className="mt-8 bg-gold text-black hover:bg-gold-light" data-testid="retry-payment-button">
                  <a href={order.checkout_url}>Voltar para o pagamento</a>
                </Button>
              )}
            </>
          )}
        </div>

        <div className="border border-white/10 rounded-lg p-8 bg-[#111111] space-y-6" data-testid="order-details">
          <div className="space-y-3">
            {order.items.map((i, idx) => (
              <div key={idx} className="text-sm">
                <div className="flex justify-between gap-4">
                  <span className="text-zinc-300">{i.quantity}× {i.name} <span className="text-zinc-500">({i.variation_name})</span></span>
                  <span className="text-zinc-200 shrink-0">{money(i.unit_price * i.quantity)}</span>
                </div>
                {paid && (i.serials || []).length > 0 && (
                  <p className="text-xs text-gold/80 font-mono mt-1" data-testid={`serial-${idx}`}>
                    Nº de série: {i.serials.map((s) => `#${String(s).padStart(4, "0")}`).join(", ")} — você foi o{i.quantity > 1 ? "s" : ""} {i.serials.map((s) => `${s}º`).join(" e ")} a comprar este produto
                  </p>
                )}
              </div>
            ))}
          </div>
          <div className="border-t border-white/10 pt-4 space-y-2 text-sm">
            <div className="flex justify-between text-zinc-400"><span>Subtotal</span><span>{money(order.subtotal)}</span></div>
            {order.discount > 0 && <div className="flex justify-between text-gold"><span>Desconto</span><span>-{money(order.discount)}</span></div>}
            <div className="flex justify-between text-zinc-400"><span>Frete</span><span>{order.shipping ? money(order.shipping) : "Grátis"}</span></div>
            <div className="flex justify-between text-lg pt-2"><span>Total</span><span className="text-gold font-semibold" data-testid="order-total">{money(order.total)}</span></div>
          </div>

          {paid && order.originality_code && (
            <div className="border border-gold/30 rounded-md p-5 bg-black/40" data-testid="originality-box">
              <div className="flex items-start justify-between gap-3 mb-2">
                <p className="label-caps flex items-center gap-2"><ShieldCheck size={13} /> Código de autenticidade</p>
                <Button asChild size="sm" variant="outline" className="border-gold/40 text-gold hover:bg-amber-500/10 shrink-0" data-testid="download-invoice-button">
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${order.id}/nota.pdf`} target="_blank" rel="noopener noreferrer">Nota PDF</a>
                </Button>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-xl text-gold tracking-wider" data-testid="originality-code">{order.originality_code}</span>
                <button onClick={copyCode} className="text-zinc-400 hover:text-gold transition-colors" data-testid="copy-code-button" aria-label="Copiar código"><Copy size={15} /></button>
              </div>
              <p className="text-xs text-zinc-500 mt-2">
                Vinculado a {order.customer_name}. Garantia até {order.warranty_until ? new Date(order.warranty_until).toLocaleDateString("pt-BR") : "—"}.
                Verifique em <Link to="/autenticidade" className="text-gold hover:underline">/autenticidade</Link>.
              </p>
            </div>
          )}

          {paid && order.items.some((i) => i.digital_url) && (
            <div className="border border-white/10 rounded-md p-5 bg-black/40" data-testid="digital-delivery-box">
              <p className="label-caps mb-3 flex items-center gap-2"><Download size={13} /> Entrega digital</p>
              {order.items.filter((i) => i.digital_url).map((i, idx) => (
                <div key={idx} className="flex items-center justify-between gap-3 py-2">
                  <span className="text-sm text-zinc-300">{i.name}</span>
                  <Button asChild size="sm" variant="outline" data-testid={`digital-download-${idx}`}
                    className="border-gold/40 text-gold hover:bg-amber-500/10">
                    <a href={i.digital_url} target="_blank" rel="noopener noreferrer">Acessar link</a>
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="text-center mt-10">
          <Link to="/" className="text-sm text-gold hover:underline" data-testid="back-to-store-link">Voltar à loja</Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
